import React, { useContext, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import { IGroup } from '../../../../shared/services/group/group.model';
import GroupService from '../../../../shared/services/group/group.service';
import { Box, Button, Chip, Grid, InputAdornment, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery } from '@mui/material'
import { Add, GridView, MenuOutlined, SearchOutlined } from '@mui/icons-material'
import { AddEmissionGroupPickerDialog } from '../../../dialogs/emissions/add-emission-group-picker-dialog';
import { TooltipButton } from '../../../presentation-components/miscellaneous-components/tooltip-button';
import { EmissionsGroupCard } from '../../../presentation-components/emissons-components/overview/emissions-display/emissions-group-card';
import { useIsReportPeriodLocked } from '../../../../hooks/use-is-report-period-locked';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { formatGroupTotal, getGroupTotalBasisLabel, getGroupTotalTonnes } from '../../../../shared/utils/group-total-utils';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import OrganisationService from '../../../../shared/services/organisation/organisation.service';
import gaiaTheme from '../../../../root-components/theme';
import { ClosedReportPeriodMessage } from '../../../presentation-components/miscellaneous-components/closed-report-period-message';
import { StartNewReportPeriodDialog } from '../../../dialogs/settings/report-period/start-new-report-period-dialog';
import { LoadingPage } from '../../loading-page';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageTitle } from '../../../presentation-components/display-elements-components/page-title';
import { useHasOpenReportPeriod } from '../../../../hooks/use-has-open-report-period';
import { useRightChromeLayout } from '../../../../context/right-chrome-layout/right-chrome-layout-context';

export const EmissionsOverviewPage = () => {
  // The completion tick is derived, never hand-set: a closed period, data present, and no
  // incomplete tasks. On an open period the work is by definition still in motion.
  const isReportPeriodLockedForTick = useIsReportPeriodLocked()
  const showGroupTick = (group: { hasData?: boolean, actionsTotal: number, actionsCompleted: number }) =>
    isReportPeriodLockedForTick === true && !!group.hasData && group.actionsCompleted === group.actionsTotal
  const navigate = useNavigate();
  //Services
  // Each card shows its group's total for the period, on the organisation's default Scope 2 basis.
  // A group with nothing recorded shows no figure rather than a row of zeros.
  const groupTotalProps = (group: IGroup) => {
    const defaultMarketBased = userDetailsContext.userDetails?.organisation?.defaultMarketBased
    const tonnes = getGroupTotalTonnes(group.totalEmissions, defaultMarketBased)
    const hasTotal = tonnes != null && tonnes > 0
    return {
      totalLabel: hasTotal ? formatGroupTotal(tonnes) : null,
      totalBasisLabel: hasTotal ? getGroupTotalBasisLabel(group.scope, defaultMarketBased) : null,
    }
  }
  const groupService = GroupService.getInstance();
  const organisationService = new OrganisationService()
  const reportPeriodContext = useContext(ReportPeriodContext);
  const userNotificationsContext = useContext(UserNotificationsContext);
  const userDetailsContext = useContext(UserDetailsContext);
  //State
  const [groups, setGroups] = useState<IGroup[]>([]);
  const [searchQuery, setSearchQuery] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addEmissionDialogOpen, setAddEmissionDialogOpen] = useState(false);
  const [showInactive, setShowInactive] = useState<boolean>(false)
  const [page, setPage] = React.useState<string>('/emissions/overview');
  //Loading State
  const [isLoading, setIsLoading] = useState(true);
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  const { copilotOpen, actionsPaneOpen } = useRightChromeLayout();
  const narrowForSidePanels = copilotOpen || actionsPaneOpen;
  /** 3 columns (lg=4) by default; 2 × ~50% columns (lg=6) when G.AI or actions pane reduces main width */
  const groupCardGrid = narrowForSidePanels
    ? ({ xs: 12, md: 6, lg: 6 } as const)
    : ({ xs: 12, md: 6, lg: 4 } as const);
  //Hooks 
  const hasOpenReport = useHasOpenReportPeriod([
    reportPeriodContext.allReportPeriods,
    reportPeriodContext.lastSelectedReportId,
    dialogOpen,
  ]);
  useEffect(() => {
    getGroups();
  }, [dialogOpen, reportPeriodContext.allReportPeriods, reportPeriodContext.lastSelectedReportId])

  const getGroups = () => {
    if (reportPeriodContext.lastSelectedReportId) {
      groupService.getGroupsAndActionInfoForReportPeriod(reportPeriodContext.lastSelectedReportId)
        .then(response => {
          setGroups(response)
          setIsLoading(false)
        })
        .catch(err => {
          console.log(err)
        })
    } else {
      setIsLoading(false)
    }
  }

  const searchHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const filteredCategories = groups.filter(group =>
    group.title.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const filteredScope1 = filteredCategories.filter(group => group.scope === '1')
  const filteredScope2 = filteredCategories.filter(group => group.scope === '2')
  const filteredScope3Active = filteredCategories.filter(group => group.scope === '3' && group.isTagged === true)
  const filteredScope3Inactive = filteredCategories.filter(group => group.scope === '3' && group.isTagged === false)

  const showHandler = () => {
    setShowInactive(prevShowInactive => !prevShowInactive)
  }

  const errorHandler = (groupId: string) => {
    if (userDetailsContext.userDetails?.roles.some((role) => role === 'admin')) {
      userNotificationsContext.openDialog({
        active: true,
        title: 'Confirmation',
        message: 'The selected group is inactive. Please confirm you wish to make this group active.',
        buttonCopy: 'Continue', cancelButtonCopy: 'Cancel'
      })
        .then(response => {
          if (response) {
            organisationService.toggleGroupToOrganisation(groupId)
              .then(response => {
                userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
                getGroups();
              })
              .catch(err => {
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                console.log(err)
              })
          }
        })
    } else {
      userNotificationsContext.openDialog({
        active: true,
        title: 'Access Denied',
        message: 'The selected group is inactive. Please contact your administrator or check your company settings for more details.',
        buttonCopy: 'Ok', hideCancelButton: true
      })
    }
  }

  const pageHandler = (event: React.MouseEvent<HTMLElement>, page: string | null) => {
    if (page) {
      setPage(page);
      navigate(page)
    }
  };

  return (
    <PageOuterContainer>
      <Stack
        direction={isMobileMode ? 'column' : 'row'}
        justifyContent='space-between'
        alignItems={isMobileMode ? 'stretch' : 'center'}
        spacing={isMobileMode ? 1.5 : 0}
        sx={{ mb: 1, width: '100%' }}
      >
        <PageTitle title={'Emissions'} />
        {hasOpenReport &&
          <Stack
            direction={isMobileMode ? 'column' : 'row'}
            spacing={1.5}
            alignItems={isMobileMode ? 'stretch' : 'center'}
          >
            {/* Never disabled on load: groups arrive a moment after paint, and flipping a
                contained button from grey to green reads as a glitch. The dialog says it is
                still loading in the rare window where they have not arrived. */}
            <Button variant='contained' onClick={() => setAddEmissionDialogOpen(true)}>
              <Add />
              <Typography>Add Emission</Typography>
            </Button>
            <ToggleButtonGroup
              value={page}
              exclusive
              onChange={pageHandler}
              orientation='horizontal'
              sx={{
                width: isMobileMode ? '100%' : 'auto',
                display: 'flex',
                justifyContent: 'flex-end',
                alignSelf: isMobileMode ? 'stretch' : 'auto',
                '& .MuiToggleButton-root': isMobileMode
                  ? { flex: 1, py: 1.125, minHeight: 44 }
                  : {},
              }}
            >
              <ToggleButton
                value="/emissions/overview"
                aria-label="Group view"
                sx={{ width: isMobileMode ? undefined : '50px', pt: 0.5, pb: 0.5, textTransform: 'capitalize' }}
              >
                <GridView />
              </ToggleButton>
              <ToggleButton
                value="/emissions/overview/table"
                aria-label="Table view"
                sx={{ width: isMobileMode ? undefined : '50px', pt: 0.5, pb: 0.5, textTransform: 'none' }}
              >
                <MenuOutlined />
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>}
      </Stack>
      {addEmissionDialogOpen &&
        <AddEmissionGroupPickerDialog
          open={addEmissionDialogOpen}
          closeHandler={() => setAddEmissionDialogOpen(false)}
          groups={groups}
        />}
      <LoadingPage pageReady={!isLoading}>
        {hasOpenReport !== null && hasOpenReport === false &&
          <Box>
            <ClosedReportPeriodMessage openDialog={() => { setDialogOpen(true) }} />
            {dialogOpen && <StartNewReportPeriodDialog open={dialogOpen} closeHandler={() => { setDialogOpen(false) }} />}
          </Box>}
        {hasOpenReport &&
          <Box>
            <Grid container justifyContent="flex-end" sx={{ mt: isMobileMode ? 2 : 0, pt: 1 }}>
              <Grid item xl={3} lg={3} md={3} sm={12} xs={12}
                sx={{ mb: isMobileMode ? 2 : 0, display: 'flex', flexDirection: 'row', alignItems: 'flex-end', width: '100%' }}>
                <TextField sx={{ flex: 1 }} size='small' fullWidth variant='outlined' placeholder='Search' value={searchQuery}
                  onChange={searchHandler}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchOutlined />
                      </InputAdornment>)
                  }}
                />
              </Grid>
            </Grid>
            {filteredScope1.length > 0 &&
              <Box>
                <Stack display='flex' flexDirection={isMobileMode ? 'column' : 'row'} alignItems={isMobileMode ? 'flex-start' : 'center'} sx={{ mb: 3 }}>
                  <Typography variant='h5' sx={{ fontWeight: 700, pr: 1, color: 'text.primary' }}> Scope 1 </Typography>
                  <Stack display='flex' flexDirection='row' alignItems='center'>
                    <Typography variant='h5' sx={{ color: 'text.primary' }}> Direct Emissions </Typography>
                    {!isMobileMode && <TooltipButton
                      title='Scope 1'
                      text='Direct greenhouse gas emissions from owned or controlled sources, such as company vehicles and on-site fuel combustion. These emissions are directly generated by your organisation’s activities.'
                      placement='right'
                    />}
                  </Stack>
                </Stack>
                <Stack>
                  <Grid container spacing={4}>
                    {filteredScope1.map((group, index) => {
                      return (
                        <Grid item {...groupCardGrid} key={index}>
                          <Link to={`/emissions/overview/group/${group.id}`} style={{ textDecoration: 'none' }}>
                            <EmissionsGroupCard
                              showCompletedTick={showGroupTick(group)}
                              {...groupTotalProps(group)}
                              icon={group.icon}
                              title={group.title}
                              description={group.subtitle}
                              actionsCompleted={group.actionsCompleted}
                              actionsTotal={group.actionsTotal} />
                          </Link>
                        </Grid>
                      )
                    })}
                  </Grid>
                </Stack>
              </Box>}
            {filteredScope2.length > 0 &&
              <Box>
                <Stack display='flex' flexDirection={isMobileMode ? 'column' : 'row'} alignItems={isMobileMode ? 'flex-start' : 'center'} sx={{ mt: 6, mb: 3 }}>
                  <Typography variant='h5' sx={{ fontWeight: 700, pr: 1, color: 'text.primary' }}> Scope 2 </Typography>
                  <Stack display='flex' flexDirection='row' alignItems='center'>
                    <Typography variant='h5' sx={{ color: 'text.primary' }}> Indirect Emissions - Owned </Typography>
                    {!isMobileMode && <TooltipButton
                      title='Scope 2'
                      text='Indirect emissions from the generation of purchased electricity, steam, heating, and cooling consumed by your organisation. Although these emissions occur off-site, they are a result of your organisation’s energy use.'
                      placement='right'
                    />}
                  </Stack>
                </Stack>
                <Stack>
                  <Grid container spacing={4}>
                    {filteredScope2.map((group, index) => {
                      return (
                        <Grid item {...groupCardGrid} key={index}>
                          <Link to={`/emissions/overview/group/${group.id}`} style={{ textDecoration: 'none' }}>
                            <EmissionsGroupCard
                              key={index}
                              showCompletedTick={showGroupTick(group)}
                              {...groupTotalProps(group)}
                              icon={group.icon}
                              title={group.title}
                              description={group.subtitle}
                              actionsCompleted={group.actionsCompleted}
                              actionsTotal={group.actionsTotal} />
                          </Link>
                        </Grid>
                      )
                    })}
                  </Grid>
                </Stack>
              </Box>}
            {filteredScope3Active.length > 0 && <>
              <Box>
                <Stack display='flex' flexDirection={isMobileMode ? 'column' : 'row'} alignItems={isMobileMode ? 'flex-start' : 'center'} sx={{ mt: 6, mb: 3 }}>
                  <Typography variant='h5' sx={{ fontWeight: 700, pr: 1, color: 'text.primary' }}> Scope 3 </Typography>
                  <Stack display='flex' flexDirection='row' alignItems='center'>
                    <Typography variant='h5' sx={{ color: 'text.primary' }}> Indirect Emissions - Not Owned </Typography>
                    {!isMobileMode && <TooltipButton
                      title='Scope 3'
                      text='Indirect emissions that occur throughout the lifecyle of your organisation’s activities, including upstream activities like purchased goods and services, as well as downstream activities such as product use and disposal. These emissions are often the largest part of an organisation’s carbon footprint but are not directly controlled by it.'
                      placement='right'
                    />}
                  </Stack>
                </Stack>
                {filteredScope3Active.filter(scope => scope.direction === 'Upstream').length > 0 && <>
                  <Chip variant='outlined' color='primary' sx={{ mb: 2 }} label={<Typography sx={{ color: 'primary.main' }}>Upstream</Typography>} />
                  <Stack>
                    <Grid container spacing={4} sx={{ mb: 4 }}>
                      {filteredScope3Active.filter(scope => scope.direction === 'Upstream').map((group, index) => {
                        return (
                          <Grid item {...groupCardGrid} key={index} >
                            <Link to={`/emissions/overview/group/${group.id}`} style={{ textDecoration: 'none' }}>
                              <EmissionsGroupCard
                                key={index}
                                icon={group.icon}
                                title={group.title}
                                showCompletedTick={showGroupTick(group)}
                              {...groupTotalProps(group)}
                                description={group.subtitle}
                                actionsCompleted={group.actionsCompleted}
                                actionsTotal={group.actionsTotal} />
                            </Link>
                          </Grid>
                        )
                      })}
                    </Grid>
                  </Stack>
                </>}
                {filteredScope3Active.filter(scope => scope.direction === 'Downstream').length > 0 && <>
                  <Chip variant='outlined' color='primary' sx={{ mb: 2 }} label={<Typography sx={{ color: 'primary.main' }}>Downstream</Typography>} />
                  <Stack>
                    <Grid container spacing={4}>
                      {filteredScope3Active.filter(scope => scope.direction === 'Downstream').map((group, index) => {
                        return (
                          <Grid item {...groupCardGrid} key={index} >
                            <Link to={`/emissions/overview/group/${group.id}`} style={{ textDecoration: 'none' }}>
                              <EmissionsGroupCard
                                key={index}
                                icon={group.icon}
                                title={group.title}
                                showCompletedTick={showGroupTick(group)}
                              {...groupTotalProps(group)}
                                description={group.subtitle}
                                actionsCompleted={group.actionsCompleted}
                                actionsTotal={group.actionsTotal} />
                            </Link>
                          </Grid>
                        )
                      })}
                    </Grid>
                  </Stack>
                </>}
              </Box>
              {filteredScope3Inactive.length > 0 && <Box sx={{ display: 'flex', direction: 'row', justifyContent: 'flex-end', mt: 2 }}>
                <Button onClick={showHandler}><Typography>{showInactive ? 'Hide All Scope 3 Categories' : 'Show All Scope 3 Categories'}</Typography></Button>
              </Box>}
            </>}
            {showInactive && <>
              {filteredScope3Inactive.filter(scope => scope.direction === 'Upstream').length > 0 && <>
                <Chip sx={{ mb: 2, mt: 2 }} label={<Typography sx={{ color: 'text.primary' }}>Upstream</Typography>} />
                <Stack>
                  <Grid container spacing={4} sx={{ mb: 4 }}>
                    {filteredScope3Inactive.filter(scope => scope.direction === 'Upstream').map((group, index) => {
                      return (
                        <Grid item {...groupCardGrid} key={index} onClick={() => errorHandler(group.id)}>
                          <EmissionsGroupCard
                            key={index}
                            icon={group.icon}
                            title={group.title}
                            showCompletedTick={showGroupTick(group)}
                              {...groupTotalProps(group)}
                            description={group.subtitle}
                            isInactive={true}
                            actionsTotal={0} />
                        </Grid>
                      )
                    })}
                  </Grid>
                </Stack>
              </>}
              {filteredScope3Inactive.filter(scope => scope.direction === 'Downstream').length > 0 && <>
                <Chip sx={{ mb: 2 }} label={<Typography sx={{ color: 'text.primary' }}>Downstream</Typography>} />
                <Stack>
                  <Grid container spacing={4}>
                    {filteredScope3Inactive.filter(scope => scope.direction === 'Downstream').map((group, index) => {
                      return (
                        <Grid item {...groupCardGrid} key={index} onClick={() => errorHandler(group.id)}>
                          <EmissionsGroupCard
                            key={index}
                            icon={group.icon}
                            title={group.title}
                            showCompletedTick={showGroupTick(group)}
                              {...groupTotalProps(group)}
                            description={group.subtitle}
                            isInactive={true}
                            actionsTotal={0} />
                        </Grid>
                      )
                    })}
                  </Grid>
                </Stack>
              </>}
            </>}
          </Box>}
      </LoadingPage>
    </PageOuterContainer>
  )
}
